import { type RouteRecordRaw } from 'vue-router'
import { PERMISSION_READ_PORTFOLIOS, PERMISSION_WRITE_COMPANIES, PERMISSION_WRITE_COMPANY_DATA } from '@/vue/helpers/permissions'
import { check } from '../guards/permission'
import type { PortfolioWithYear, WithReportingYear } from './types'

export interface RouteParams {
    'reports.company.overview': WithReportingYear
    'reports.company.reports': WithReportingYear
    'reports.portfolio.overview': PortfolioWithYear
    'reports.portfolio.report': PortfolioWithYear<{ report_id: string }>
    'reports.portfolio.data-gaps': PortfolioWithYear
    'reports.portfolio.saved-reports': PortfolioWithYear
    'reports.portfolio.report-history': PortfolioWithYear
}

export const reportsRoutes: RouteRecordRaw[] = [
    // Company-level reports
    {
        path: 'company/:reporting_year?',
        component: () => import('@/vue/pages/reports/ReportsCompanyLayout.vue'),
        children: [
            {
                path: '',
                redirect: { name: 'reports.company.overview' },
            },
            {
                path: 'overview',
                name: 'reports.company.overview',
                component: () => import('@/vue/pages/reports/company/overview/OverviewPage.vue'),
                meta: {
                    title: 'general.reports',
                    feature: {
                        flag: 'reports-section',
                        fallbackRoute: 'home',
                    },
                },
            },
            {
                path: 'reports',
                name: 'reports.company.reports',
                component: () => import('@/vue/pages/reports/company/reports/CompanyReportsPage.vue'),
                meta: {
                    title: 'general.reports',
                    feature: {
                        flag: 'reports-section',
                        fallbackRoute: 'home',
                    },
                },
                beforeEnter: check([PERMISSION_WRITE_COMPANIES, PERMISSION_WRITE_COMPANY_DATA], 'reports.company.overview'),
            },
        ],
    },
    // Portfolio-level reports
    {
        path: 'portfolio/:portfolio_id/:reporting_year',
        component: () => import('@/vue/pages/reports/ReportsPortfolioLayout.vue'),
        beforeEnter: check([PERMISSION_READ_PORTFOLIOS], 'dashboard'),
        children: [
            {
                path: '',
                redirect: { name: 'reports.portfolio.overview' },
            },
            {
                path: 'overview',
                name: 'reports.portfolio.overview',
                component: () => import('@/vue/pages/reports/portfolio/overview/OverviewPage.vue'),
                meta: {
                    title: 'general.reports',
                    feature: {
                        flag: 'reports-section',
                        fallbackRoute: 'home',
                    },
                },
            },
            {
                path: 'report/:report_id',
                name: 'reports.portfolio.report',
                component: () => import('@/vue/pages/reports/portfolio/report/ReportPage.vue'),
                meta: {
                    title: 'general.reports',
                    feature: {
                        flag: 'reports-section',
                        fallbackRoute: 'home',
                    },
                },
            },
            {
                path: 'data-gaps',
                name: 'reports.portfolio.data-gaps',
                component: () => import('@/vue/pages/reports/portfolio/data-gaps/DataGapsPage.vue'),
                meta: {
                    title: 'general.alerts-label',
                    feature: {
                        flag: 'reports-section',
                        fallbackRoute: 'home',
                    },
                },
            },
            {
                path: 'saved-reports',
                name: 'reports.portfolio.saved-reports',
                component: () => import('@/vue/pages/reports/portfolio/saved-reports/SavedReportsPage.vue'),
                meta: {
                    title: 'general.sub-navigation.saved-reports',
                    feature: {
                        flag: 'reports-section',
                        fallbackRoute: 'home',
                    },
                },
            },
            {
                path: 'report-history',
                name: 'reports.portfolio.report-history',
                component: () => import('@/vue/pages/reports/portfolio/report-history/ReportHistoryPage.vue'),
                meta: {
                    title: 'general.sub-navigation.report-history',
                    feature: {
                        flag: 'reports-section',
                        fallbackRoute: 'home',
                    },
                },
            },
            {
                path: ':pathMatch(.*)*',
                redirect: { name: 'reports.portfolio.overview' },
            },
        ],
    },
]
